/**
 * Stage: canvas のマウントと表示サイズ管理。
 *
 * 設計書 10.1 / プロトタイプ `docs/prototype.html` の `#stage` + `#frame` +
 * `#canvas` + `.stage-info` + `.stage-kbd` 構造を再現する。
 *
 * v1.1 移行メモ:
 * - 旧 `state.resolution` / `state.cells` は v1.1 で削除された（spec §6）。
 *   canvas の内部解像度は `render/layout.ts` の `computeLayout(state).outerW/H`
 *   から導出される設計に変わる。Phase A 中は無難な fallback（1080²）を
 *   初期値に置き、@render-agent が computeLayout 契約を固めた段階で
 *   `stage.canvas.width = L.outerW` の形で main.ts 側から上書きする。
 * - stage-info の RES / FRAG 表示も layout 契約の確定待ち。現状は
 *   初期 canvas サイズと `fragmentCount` を表示するのみ。
 *
 * 責務:
 * - `.stage` 配下に frame > canvas を挿入
 * - ResizeObserver で stage のサイズ変化に追従し canvas の CSS 表示サイズを
 *   contain-fit で決定する（`fitCanvas` 相当）
 * - stage-info / stage-kbd を描画
 *
 * 依存方向ルール:
 * render モジュールにある `fitCanvas` は UI からは import しない
 * （設計書 5.1: `ui` は `render` を直接呼ばない）。そのため fit 計算の
 * ロジックだけは本ファイル内に複製する（~10 行）。両者に齟齬が出ない
 * ようプロトタイプの実装を起点とする。
 */

import type { AppState, Store } from '../state';

/** stage の周辺 padding。プロトタイプの `padding: 48px` + 余裕 = 96px。 */
const STAGE_FIT_PADDING = 96;

/**
 * Phase A 中の canvas 初期解像度。@render-agent が layout 契約を固めた
 * ら computeLayout 経由で上書きされる（main.ts が `stage.canvas.width`
 * を書き換える想定）。
 */
const FALLBACK_CANVAS_SIZE = 1080;

/** `mountStage` の戻り値。 */
export interface StageHandle {
  /** main.ts から `ctx` を直接受け取って描画できるように公開する。 */
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  /** teardown: observer 解除 + DOM 撤去。 */
  destroy: () => void;
  /**
   * stage サイズ変化時に呼ぶ callback を登録する。
   * 実装上は ResizeObserver → fit → callback の順で発火する。
   * main.ts が「サイズ変化 → 再描画」をつなぐのに使う。
   */
  onResize: (cb: () => void) => void;
}

/**
 * canvas の CSS 表示サイズを contain-fit で決める。
 *
 * プロトタイプ `fitCanvas` と同一のロジック。`stage` の可視領域から
 * padding を引き、canvas の内部解像度と比較して縮小率を算出する。
 * 拡大（scale > 1）は行わない — 1px 未満の解像度にはマッピングしない。
 */
function fitCanvas(canvas: HTMLCanvasElement, stage: HTMLElement): void {
  const rect = stage.getBoundingClientRect();
  const availW = Math.max(0, rect.width - STAGE_FIT_PADDING);
  const availH = Math.max(0, rect.height - STAGE_FIT_PADDING);
  const scale = Math.min(availW / canvas.width, availH / canvas.height, 1);
  canvas.style.width = `${canvas.width * scale}px`;
  canvas.style.height = `${canvas.height * scale}px`;
}

/**
 * Stage をマウントする。
 *
 * @param root - `.stage` 要素（index.html の `<main class="stage">`）
 * @param store - グローバルストア
 * @returns `StageHandle`
 */
export function mountStage(root: HTMLElement, store: Store<AppState>): StageHandle {
  // ── DOM 構築 ─────────────────────────────────────────────────
  const frame = document.createElement('div');
  frame.className = 'frame';

  const canvas = document.createElement('canvas');
  canvas.width = FALLBACK_CANVAS_SIZE;
  canvas.height = FALLBACK_CANVAS_SIZE;

  // 2D context が取れない環境は想定外（全対象ブラウザで取得可能）。
  // null を返すとレンダラが組めないため即座に throw する。
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is unavailable in this environment');
  }

  frame.appendChild(canvas);
  root.appendChild(frame);

  // ── stage-info（RES / FRAG 表示） ────────────────────────────
  // TODO(@render-agent): computeLayout(state) 契約確定後に resValue の
  // 更新を wire する（outerW × outerH を反映）。現状は初期 canvas サイズ
  // をそのまま表示する。
  const info = document.createElement('div');
  info.className = 'stage-info';
  const resSpan = document.createElement('span');
  const resStrong = document.createElement('strong');
  resStrong.textContent = 'RES';
  const resValue = document.createElement('span');
  resValue.textContent = `${canvas.width} × ${canvas.height}`;
  resSpan.appendChild(resStrong);
  resSpan.appendChild(resValue);

  const fragSpan = document.createElement('span');
  const fragStrong = document.createElement('strong');
  fragStrong.textContent = 'FRAG';
  const fragValue = document.createElement('span');
  // TODO(@render-agent): sceneCache 確定後は cells 数を表示する。
  // Phase A 中は fragmentCount パラメータの表示のみ。
  fragValue.textContent = String(store.get().fragmentCount);
  fragSpan.appendChild(fragStrong);
  fragSpan.appendChild(fragValue);

  info.appendChild(resSpan);
  info.appendChild(fragSpan);
  root.appendChild(info);

  // ── stage-kbd（ショートカット表示） ──────────────────────────
  const kbd = document.createElement('div');
  kbd.className = 'stage-kbd';
  const spaceSpan = document.createElement('span');
  spaceSpan.innerHTML = '<kbd>SPACE</kbd>Detonate';
  const rSpan = document.createElement('span');
  rSpan.innerHTML = '<kbd>R</kbd>Reset';
  kbd.appendChild(spaceSpan);
  kbd.appendChild(rSpan);
  root.appendChild(kbd);

  // ── resize 追従 ──────────────────────────────────────────────
  let resizeCb: (() => void) | null = null;

  const relayout = (): void => {
    fitCanvas(canvas, root);
    resizeCb?.();
  };

  const observer = new ResizeObserver(() => {
    relayout();
  });
  observer.observe(root);

  // window.resize は ResizeObserver 発火を含むため本来不要だが、
  // レイアウト初期化時にスタイル計算が整っていないブラウザ向けに保険。
  window.addEventListener('resize', relayout);

  // 初回 fit
  relayout();

  // ── store 購読（Phase A: fragmentCount の表示更新のみ） ────────
  // NOTE: aspectRatio 変化に伴う canvas resize は spec §3.1 の
  // computeLayout 経由で main.ts から `canvas.width` を書き換える
  // 設計になる。本ファイルはその書き換えを受け入れる構造のみ保つ。
  const unsubscribe = store.subscribe((state, prev) => {
    if (state.fragmentCount !== prev.fragmentCount) {
      fragValue.textContent = String(state.fragmentCount);
    }
  });

  return {
    canvas,
    ctx,
    destroy: (): void => {
      unsubscribe();
      observer.disconnect();
      window.removeEventListener('resize', relayout);
      frame.remove();
      info.remove();
      kbd.remove();
    },
    onResize: (cb: () => void): void => {
      resizeCb = cb;
    },
  };
}
